import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { http, fmtRON, JUDETE } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Home, Package, ExternalLink } from "lucide-react";

const DELIVERY_OPTIONS = [
  {
    id: "curier",
    label: "Curier la domiciliu",
    description: "Livrare 24-48h la ușa ta",
    price: 20,
    icon: Home,
    mapUrl: null,
  },
  {
    id: "easybox",
    label: "Easybox Sameday",
    description: "Ridici de la cel mai apropiat locker, 24/7",
    price: 15,
    icon: Package,
    mapUrl: "https://sameday.ro/easybox/",
    mapLabel: "Vezi harta Easybox",
    locker_type: "easybox",
  },
  {
    id: "fanbox",
    label: "FanBox Fan Courier",
    description: "Ridici de la cel mai apropiat locker, 24/7",
    price: 15,
    icon: Package,
    mapUrl: "https://www.fancourier.ro/fanbox/",
    mapLabel: "Vezi harta FanBox",
    locker_type: "fanbox",
  },
];

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", city: "", county: "", postal_code: "",
    delivery_method: "curier",
    locker_code: "", locker_address: "",
    payment_method: "ramburs", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const selected = DELIVERY_OPTIONS.find((o) => o.id === form.delivery_method) || DELIVERY_OPTIONS[0];
  const isLocker = !!selected.locker_type;

  const shipping = useMemo(() => {
    if (subtotal === 0) return 0;
    if (subtotal >= 150) return 0;
    return selected.price;
  }, [subtotal, selected]);
  const total = subtotal + shipping;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset locker fields when switching delivery method
  const changeDeliveryMethod = (id) => {
    setForm((f) => ({ ...f, delivery_method: id, locker_code: "", locker_address: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Coșul tău este gol"); return; }
    if (isLocker && !form.locker_address.trim()) {
      toast.error("Te rog scrie adresa locker-ului.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        payment_method: form.payment_method,
        notes: form.notes,
        delivery_method: form.delivery_method,
        items: items.map((i) => ({ product_id: i.product_id, title: i.title, price: i.price, quantity: i.quantity, cover_image: i.cover_image })),
        subtotal, shipping, total,
        ...(isLocker
          ? { locker_type: selected.locker_type, locker_code: form.locker_code || null, locker_address: form.locker_address }
          : { address: form.address, city: form.city, county: form.county, postal_code: form.postal_code }),
      };
      const { data } = await http.post("/orders", payload);
      clear();
      setConfirmedOrder(data);
    } catch (err) {
      toast.error("Nu am putut finaliza comanda. Încearcă din nou.");
    } finally { setLoading(false); }
  };

  if (confirmedOrder) {
    return (
      <div className="py-24 min-h-[60vh]" data-testid="order-confirmed-page">
        <div className="container-vdz max-w-xl text-center">
          <div className="eyebrow mb-3">Comandă finalizată</div>
          <h1 className="font-serif-display text-4xl md:text-5xl" style={{ color: "var(--vdz-navy)" }}>Mulțumim, {confirmedOrder.first_name}! ✨</h1>
          <p className="mt-6 opacity-80 leading-relaxed">
            Comanda ta <strong className="gold-text font-serif-display text-xl" data-testid="order-number">{confirmedOrder.order_number}</strong> a fost înregistrată.
            Vei primi un email de confirmare cât de curând. Vei plăti ramburs la curier{confirmedOrder.locker_type ? " / locker" : ""}.
          </p>
          <button onClick={() => nav("/")} className="btn-gold mt-10" data-testid="continue-shopping-button">Continuă să visezi</button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-24 min-h-[60vh]">
        <div className="container-vdz max-w-xl text-center">
          <h1 className="font-serif-display text-4xl" style={{ color: "var(--vdz-navy)" }}>Coșul tău este gol</h1>
          <p className="mt-4 opacity-70">Alege mai întâi cărțile tale favorite.</p>
          <button onClick={() => nav("/carti")} className="btn-gold mt-8">Vezi cărțile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-14" data-testid="checkout-page">
      <div className="container-vdz">
        <div className="eyebrow mb-3">Finalizare comandă</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-12" style={{ color: "var(--vdz-navy)" }}>Datele tale.</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--vdz-navy)" }}>Date de contact</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input className="vdz-input" placeholder="Prenume" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="checkout-first-name" />
                <input className="vdz-input" placeholder="Nume" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required data-testid="checkout-last-name" />
                <input className="vdz-input" type="email" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="checkout-email" />
                <input className="vdz-input" placeholder="Telefon (ex: 0712...)" value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="checkout-phone" />
              </div>
            </section>

            <section>
              <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--vdz-navy)" }}>Metodă de livrare</h2>
              <div className="space-y-3" data-testid="delivery-methods">
                {DELIVERY_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = form.delivery_method === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className="flex items-center gap-4 p-4 border cursor-pointer transition-all"
                      style={{
                        borderColor: active ? "var(--vdz-gold)" : "var(--vdz-cream-border)",
                        background: active ? "var(--vdz-cream-warm)" : "white",
                      }}
                      data-testid={`delivery-method-${opt.id}`}
                    >
                      <input type="radio" name="delivery" checked={active} onChange={() => changeDeliveryMethod(opt.id)} className="shrink-0" />
                      <Icon size={22} className="shrink-0" style={{ color: active ? "var(--vdz-gold)" : "var(--vdz-navy)" }} />
                      <div className="flex-1">
                        <div className="font-serif-display text-lg leading-tight" style={{ color: "var(--vdz-navy)" }}>{opt.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">{opt.description}</div>
                      </div>
                      <div className="font-serif-display text-lg shrink-0" style={{ color: subtotal >= 150 ? "var(--vdz-gold)" : "var(--vdz-navy)" }}>
                        {subtotal >= 150 ? "Gratuit" : `${opt.price} RON`}
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Locker manual input */}
            {isLocker && (
              <section data-testid="locker-picker-section">
                <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--vdz-navy)" }}>Alege locker-ul tău</h2>
                <p className="text-sm opacity-75 mb-5 leading-relaxed">
                  Deschide harta oficială {selected.label}, găsește locker-ul cel mai apropiat de tine și copiază adresa (și codul lui, dacă apare) în câmpurile de mai jos.
                </p>

                <a href={selected.mapUrl} target="_blank" rel="noreferrer"
                   className="btn-dark inline-flex items-center gap-2 mb-5"
                   data-testid="locker-open-map">
                  <MapPin size={14} /> {selected.mapLabel} <ExternalLink size={12} />
                </a>

                <div className="space-y-3">
                  <input
                    className="vdz-input"
                    placeholder={selected.id === "easybox" ? "Adresa Easybox (ex: Mega Image Dr. Felix nr. 15, București)" : "Adresa FanBox (ex: OMV Barbu Văcărescu 162, București)"}
                    value={form.locker_address}
                    onChange={(e) => update("locker_address", e.target.value)}
                    required
                    data-testid="locker-address-input"
                  />
                  <input
                    className="vdz-input"
                    placeholder="Numele / codul locker-ului (opțional, ex: BUC001 sau easybox Mega Dr. Felix)"
                    value={form.locker_code}
                    onChange={(e) => update("locker_code", e.target.value)}
                    data-testid="locker-code-input"
                  />
                </div>
                <p className="text-xs opacity-60 mt-3">
                  Vei primi un cod de ridicare prin SMS pe numărul <strong>{form.phone || "introdus mai sus"}</strong> când coletul ajunge în locker.
                </p>
              </section>
            )}

            {/* Home address */}
            {!isLocker && (
              <section data-testid="home-address-section">
                <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--vdz-navy)" }}>Adresă livrare</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input className="vdz-input sm:col-span-2" placeholder="Strada, număr, bloc, apartament" value={form.address} onChange={(e) => update("address", e.target.value)} required data-testid="checkout-address" />
                  <input className="vdz-input" placeholder="Oraș" value={form.city} onChange={(e) => update("city", e.target.value)} required data-testid="checkout-city" />
                  <select className="vdz-input" value={form.county} onChange={(e) => update("county", e.target.value)} required data-testid="checkout-county">
                    <option value="">Alege județul</option>
                    {JUDETE.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                  <input className="vdz-input" placeholder="Cod poștal" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} required data-testid="checkout-postal-code" />
                </div>
              </section>
            )}

            <section>
              <h2 className="font-serif-display text-2xl mb-3" style={{ color: "var(--vdz-navy)" }}>Observații (opțional)</h2>
              <textarea className="vdz-input" placeholder="Instrucțiuni suplimentare pentru curier..." value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} data-testid="checkout-notes" />
            </section>

            <section>
              <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--vdz-navy)" }}>Metodă de plată</h2>
              <label className="flex items-center gap-4 p-4 border cursor-pointer" style={{ borderColor: "var(--vdz-gold)", background: "var(--vdz-cream-warm)" }}>
                <input type="radio" checked readOnly />
                <div>
                  <div className="font-serif-display text-lg">Ramburs la curier</div>
                  <div className="text-xs opacity-70">Plătești în numerar direct la primirea coletului, sau cu cardul la easybox sau fanbox.</div>
                </div>
              </label>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 p-6 border" style={{ background: "white", borderColor: "var(--vdz-cream-border)" }}>
              <div className="eyebrow mb-3">Sumar comandă</div>
              <div className="space-y-4 max-h-72 overflow-y-auto mb-5">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3">
                    <img src={i.cover_image} alt="" className="w-14 h-20 object-cover" />
                    <div className="flex-1 text-sm">
                      <div className="font-serif-display text-base leading-tight">{i.title}</div>
                      <div className="opacity-70 text-xs mt-1">× {i.quantity}</div>
                    </div>
                    <div className="font-serif-display text-base">{fmtRON(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 space-y-2 text-sm" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <div className="flex justify-between"><span>Subtotal</span><span>{fmtRON(subtotal)}</span></div>
                <div className="flex justify-between">
                  <span>Livrare <span className="opacity-60 text-xs">({selected.label.split(" ")[0]})</span></span>
                  <span>{shipping === 0 ? "Gratuit" : fmtRON(shipping)}</span>
                </div>
                <div className="flex justify-between pt-3 mt-2 border-t" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <span className="font-serif-accent uppercase tracking-[0.18em] text-xs">Total</span>
                  <span className="font-serif-display text-2xl gold-text" data-testid="checkout-total">{fmtRON(total)}</span>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full mt-5" data-testid="checkout-submit-button">
                {loading ? "Se procesează..." : "Plasează comanda"}
              </button>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}
